import React, { useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import productData from "../Datas/productData";
import "../css/shop.scss";

const SORT_OPTIONS = [
  { value: "featured", label: "Featured" },
  { value: "price-ascending", label: "Price, low to high" },
  { value: "price-descending", label: "Price, high to low" },
  { value: "title-ascending", label: "Alphabetically, A–Z" },
  { value: "title-descending", label: "Alphabetically, Z–A" },
];

const formatPrice = (price, currency = "USD") =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
  }).format(Number(price));

function Shop() {
  const [searchParams, setSearchParams] = useSearchParams();
  const searchValue = searchParams.get("search") || "";
  const selectedCategory = searchParams.get("category") || "all";
  const requestedSort = searchParams.get("sort") || "featured";
  const selectedSort = SORT_OPTIONS.some(({ value }) => value === requestedSort)
    ? requestedSort
    : "featured";

  const categories = useMemo(
    () =>
      [...new Set(productData.map((product) => product.category).filter(Boolean))].sort(
        (a, b) => a.localeCompare(b)
      ),
    []
  );

  const visibleProducts = useMemo(() => {
    const normalizedSearch = searchValue.trim().toLocaleLowerCase();

    const filteredProducts = productData.filter((product) => {
      const matchesCategory =
        selectedCategory === "all" || product.category === selectedCategory;
      const searchableText = [product.name, product.brand, product.category]
        .filter(Boolean)
        .join(" ")
        .toLocaleLowerCase();

      return (
        matchesCategory &&
        (!normalizedSearch || searchableText.includes(normalizedSearch))
      );
    });

    return [...filteredProducts].sort((a, b) => {
      switch (selectedSort) {
        case "price-ascending":
          return Number(a.price) - Number(b.price);
        case "price-descending":
          return Number(b.price) - Number(a.price);
        case "title-ascending":
          return a.name.localeCompare(b.name);
        case "title-descending":
          return b.name.localeCompare(a.name);
        default:
          return 0;
      }
    });
  }, [searchValue, selectedCategory, selectedSort]);

  const updateParam = (name, value, defaultValue = "") => {
    setSearchParams(
      (currentParams) => {
        const nextParams = new URLSearchParams(currentParams);

        if (!value || value === defaultValue) {
          nextParams.delete(name);
        } else {
          nextParams.set(name, value);
        }

        return nextParams;
      },
      { replace: true }
    );
  };

  const resetFilters = () => {
    setSearchParams({}, { replace: true });
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const hasActiveFilters =
    Boolean(searchValue) || selectedCategory !== "all" || selectedSort !== "featured";

  return (
    <main className="shop-page">
      <section className="shop-hero" aria-labelledby="collection-title">
        <p className="shop-hero__eyebrow">Designed for curious creatives</p>
        <h1 id="collection-title">ioomparade Stationery and Desk Collection</h1>
        <p className="shop-hero__description">
          Thoughtful tools for writing, planning, making, and keeping a calmer desk.
        </p>
      </section>

      <section className="shop-catalog" aria-label="Product collection">
        <div className="shop-toolbar">
          <div className="shop-control shop-control--search">
            <label htmlFor="shop-search">Search products</label>
            <input
              id="shop-search"
              type="search"
              value={searchValue}
              placeholder="Search by product or brand"
              onChange={(event) => updateParam("search", event.target.value)}
            />
          </div>

          <div className="shop-control">
            <label htmlFor="shop-category">Category</label>
            <select
              id="shop-category"
              value={selectedCategory}
              onChange={(event) => updateParam("category", event.target.value, "all")}
            >
              <option value="all">All categories</option>
              {categories.map((category) => (
                <option key={category} value={category}>
                  {category}
                </option>
              ))}
            </select>
          </div>

          <div className="shop-control">
            <label htmlFor="shop-sort">Sort by</label>
            <select
              id="shop-sort"
              value={selectedSort}
              onChange={(event) => updateParam("sort", event.target.value, "featured")}
            >
              {SORT_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="shop-results-bar" aria-live="polite">
          <p>
            {visibleProducts.length} {visibleProducts.length === 1 ? "product" : "products"}
          </p>
          {hasActiveFilters && (
            <button type="button" onClick={resetFilters}>
              Clear filters
            </button>
          )}
        </div>

        {visibleProducts.length > 0 ? (
          <div className="product-grid">
            {visibleProducts.map((product) => {
              const primaryImage = product.images?.[0] || product.image;
              const secondaryImage = product.images?.[1];

              return (
                <article className="product-card" key={product.id}>
                  <Link
                    className="product-card__image-link"
                    to={`/shopDetails/${product.id}`}
                    onClick={scrollToTop}
                    aria-label={`View ${product.name}`}
                  >
                    <div className="product-card__image-wrap">
                      <img
                        className="product-card__image product-card__image--primary"
                        src={primaryImage}
                        alt={product.name}
                        loading="lazy"
                        decoding="async"
                      />
                      {secondaryImage && (
                        <img
                          className="product-card__image product-card__image--secondary"
                          src={secondaryImage}
                          alt=""
                          aria-hidden="true"
                          loading="lazy"
                          decoding="async"
                        />
                      )}
                      {!product.available && (
                        <span className="product-card__badge">Sold out</span>
                      )}
                    </div>
                  </Link>

                  <div className="product-card__content">
                    <p className="product-card__brand">{product.brand}</p>
                    <h2>
                      <Link to={`/shopDetails/${product.id}`} onClick={scrollToTop}>
                        {product.name}
                      </Link>
                    </h2>
                    <p className="product-card__price">
                      {formatPrice(product.price, product.currency)}
                    </p>
                  </div>
                </article>
              );
            })}
          </div>
        ) : (
          <div className="shop-empty" role="status">
            <h2>No products found</h2>
            <p>Try a different search or browse the full collection.</p>
            <button type="button" onClick={resetFilters}>
              View all products
            </button>
          </div>
        )}
      </section>
    </main>
  );
}

export default Shop;
